/**
 * Theme Provider
 *
 * Manages theme state and provides theme context
 * Integrates with Jotai atoms for theme preferences
 */

import { createContext, useContext, useEffect, PropsWithChildren, JSX } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { themeAtom, uiPreferencesAtom, accessibilityAtom } from '@/stores/atoms';
import { ThemeConfig } from '@/lib/placeholder-types';

// Theme context interface
interface ThemeContextValue {
  // Current theme mode
  theme: 'light' | 'dark';

  // Theme configuration
  config: ThemeConfig;

  // Design tokens are now managed via CSS custom properties
  tokens: Record<string, unknown>;

  // Accessibility settings
  accessibility: {
    colorBlindMode: boolean;
    highContrast: boolean;
    fontSize: 'small' | 'medium' | 'large';
    animations: boolean;
  };

  // Theme actions
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  toggleTheme: () => void;

  // Accessibility actions
  setColorBlindMode: (enabled: boolean) => void;
  setHighContrast: (enabled: boolean) => void;
  setFontSize: (size: 'small' | 'medium' | 'large') => void;
  setAnimations: (enabled: boolean) => void;
}

// Default theme configuration
const defaultThemeConfig: ThemeConfig = {
  mode: 'light',
  colors: {},
  fontSize: 'medium',
  density: 'standard',
};

// Create context
const ThemeContext = createContext<ThemeContextValue | null>(null);

// Custom hook to use theme context
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Theme provider component
export function ThemeProvider({ children }: PropsWithChildren): JSX.Element {
  // Atom values and setters
  const [uiPreferences, setUiPreferences] = useAtom(uiPreferencesAtom);
  const currentTheme = useAtomValue(themeAtom);
  const accessibility = useAtomValue(accessibilityAtom);

  // Theme configuration based on current settings
  // React Compiler automatically optimizes this computation
  const themeConfig: ThemeConfig = {
    ...defaultThemeConfig,
    mode: currentTheme,
    fontSize: accessibility.fontSize || 'medium',
    density: uiPreferences.density.toLowerCase() as 'compact' | 'standard' | 'comfortable',
  };

  // React Compiler will optimize this function reference automatically
  const applyThemeToDocument = (
    theme: 'light' | 'dark',
    accessibilitySettings: typeof accessibility
  ): void => {
    const root = document.documentElement;

    // Set theme class
    root.classList.remove('light', 'dark');
    root.classList.add(theme);

    // Set accessibility classes
    root.classList.toggle('color-blind-mode', accessibilitySettings.colorBlindMode);
    root.classList.toggle('high-contrast', accessibilitySettings.highContrast);
    root.classList.toggle('reduced-motion', !accessibilitySettings.animations);

    // Set font size
    root.classList.remove('font-small', 'font-medium', 'font-large');
    root.classList.add(`font-${accessibilitySettings.fontSize}`);

    // Set meta theme color for mobile browsers
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      const primaryColor = getComputedStyle(root).getPropertyValue('--primary').trim();
      themeColorMeta.setAttribute('content', `hsl(${primaryColor})`);
    }
  };

  // Apply theme to document
  useEffect(() => {
    applyThemeToDocument(currentTheme, accessibility);
  }, [currentTheme, accessibility]);

  // Listen for system theme changes
  useEffect(() => {
    if (uiPreferences.theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (): void => {
        // Theme atom automatically updates when system preference changes
        applyThemeToDocument(currentTheme, accessibility);
      };

      mediaQuery.addEventListener('change', handleChange);
      return (): void => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [uiPreferences.theme, currentTheme, accessibility]);

  // Theme actions
  const setTheme = (theme: 'light' | 'dark' | 'system'): void => {
    setUiPreferences(prev => ({ ...prev, theme }));
  };

  const toggleTheme = (): void => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  // Accessibility actions
  const setColorBlindMode = (enabled: boolean): void => {
    setUiPreferences(prev => ({ ...prev, colorBlindMode: enabled }));
  };

  const setHighContrast = (enabled: boolean): void => {
    setUiPreferences(prev => ({ ...prev, highContrast: enabled }));
  };

  const setFontSize = (size: 'small' | 'medium' | 'large'): void => {
    setUiPreferences(prev => ({ ...prev, fontSize: size }));
  };

  const setAnimations = (enabled: boolean): void => {
    setUiPreferences(prev => ({ ...prev, animations: enabled }));
  };

  // Context value
  const contextValue: ThemeContextValue = {
    // State
    theme: currentTheme,
    config: themeConfig,
    tokens: {},
    accessibility: {
      colorBlindMode: accessibility.colorBlindMode || false,
      highContrast: accessibility.highContrast || false,
      fontSize: accessibility.fontSize || 'medium',
      animations: accessibility.animations || true,
    },

    // Theme actions
    setTheme,
    toggleTheme,

    // Accessibility actions
    setColorBlindMode,
    setHighContrast,
    setFontSize,
    setAnimations,
  };

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}
